import { inject, injectable, injectFromBase } from 'inversify'
import { action, computed, makeObservable, observable } from 'mobx'
import { Types } from '@core/ioc/types'
import type { FormatGateway } from '@core/services/format.gateway'
import { FeedbackPresenter } from '@core/feedback/feedback.presenter'
import type { FeedbackScope } from '@core/feedback/feedback.config'
import { Router } from '@core/routing/router'
import { InboxRepository } from './inbox.repository'

export type InboxRowVm = {
  itemId: string
  headline: string
  timeLabel: string
  open: () => Promise<boolean>
}

/**
 * Where a shared resource's own detail page lives, or null when it has none
 * (see routes.tsx). Every domain feature that used to have one (assets,
 * jobs, procedures) was stripped from this scaffold, so there is currently
 * nothing to route to — always null, kept as a function so a future feature
 * that adds a detail route only has to extend this.
 */
const detailRoute = (
  resourceType: 'asset' | 'book' | 'workspace' | 'job' | 'procedure' | 'location' | 'taskType',
): { routeId: never; paramName: never } | null => {
  void resourceType
  return null
}

/**
 * The bell drawer's contents — see InboxRepository's class jsdoc for the
 * data half. Its own Presenter, not folded into ShellPresenter (chrome only)
 * for the same split ShareTargetsPresenter draws from HomePagePresenter.
 */
@injectable()
@injectFromBase()
export class InboxBellPresenter extends FeedbackPresenter {
  protected override get feedbackScope(): FeedbackScope {
    return 'inbox'
  }

  open: boolean = false

  @inject(InboxRepository)
  private inboxRepository!: InboxRepository

  @inject(Router)
  private router!: Router

  @inject(Types.IFormatGateway)
  private formatGateway!: FormatGateway

  constructor() {
    super()
    makeObservable(this, {
      open: observable,
      viewModel: computed,
      openDrawer: action,
      closeDrawer: action,
    })
  }

  get viewModel(): {
    open: boolean
    unreadCount: number
    title: string
    closeLabel: string
    isLoading: boolean
    showEmpty: boolean
    emptyTitle: string
    rows: InboxRowVm[]
  } {
    const t = this.i18nRepository.t.bind(this.i18nRepository)
    const items = this.inboxRepository.inboxListPm

    const rows: InboxRowVm[] = (items ?? []).map((item) => {
      if (item.kind === 'reminder') {
        return {
          itemId: item.itemId,
          headline: t('inbox.reminder.headline', { jobTitle: item.jobTitle }),
          timeLabel: item.dueOn,
          // No jobs feature left to open a reminder's own detail page — home is
          // the only always-reachable destination left.
          open: () => this.router.goToId('Home', {}, {}),
        }
      }

      const route = detailRoute(item.resourceType)
      return {
        itemId: item.itemId,
        headline: t('inbox.headline', { grantedBy: item.grantedBy, resourceLabel: item.resourceLabel }),
        timeLabel: this.formatGateway.formatDate(new Date(item.grantedAt), {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        }),
        open: () =>
          route
            ? this.router.goToId(route.routeId, { [route.paramName]: item.resourceId }, {})
            : // No detail route for this resource type — fall back home.
              this.router.goToId('Home', {}, {}),
      }
    })

    return {
      open: this.open,
      unreadCount: this.inboxRepository.unreadCount,
      title: t('inbox.title'),
      closeLabel: t('common.dismiss'),
      isLoading: items === null,
      showEmpty: items !== null && items.length === 0,
      emptyTitle: t('inbox.empty'),
      rows,
    }
  }

  openDrawer = (): void => {
    this.open = true
    this.inboxRepository.markSeen()
  }

  closeDrawer = (): void => {
    this.open = false
  }
}
